import SearchIcon from "@mui/icons-material/Search"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import {
    Autocomplete,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField
} from "@mui/material"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { createSearchParams, useLocation, useParams, useSearchParams } from "react-router-dom"
import Loading from "../../components/Common/Loading"
import { types } from "../../core/reducers/commonData"
import {
    getMultipleStatesWarning,
    getSearchHistory,
    getSearchHistorySynchroId,
    getSearchHistoryVendorId,
    getStatesNotFoundWarning,
    putSearchHistoryForField
} from "../../core/utils/constants"
import AdvancedSearchTable from "../AdvancedSearch/advancedSearchTable"

const normalize = (value) => value?.toString?.()?.trim?.() ?? ""

export default function MultiSearch({ useSearchHook }) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { game, deviceId: deviceIdParamRaw, action } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const deviceIdParam = normalize(deviceIdParamRaw)
    const vendorIdParam = normalize(searchParams.get("vendorId"))
    const synchroIdParam = normalize(searchParams.get("synchroId"))

    const {
        deviceId: storeDeviceId,
        stateId: storeStateId,
        synchroId: storeSynchroId,
        isMassCharge
    } = useSelector((state) => state.data.CommonData)
    const mode = useSelector((state) => state.data.SourceMode)

    const [currentDeviceId, setCurrentDeviceId] = useState(deviceIdParam)
    const [currentVendorId, setCurrentVendorId] = useState(vendorIdParam)
    const [currentSynchroId, setCurrentSynchroId] = useState(synchroIdParam)
    const [open, setOpen] = useState(false)

    const [advancedSearch, advancedSearchResponse] = useSearchHook()

    const isCharger = location.pathname.includes("/charger")
    const isRt = game === "rt"

    const setDeviceId = (deviceId) => {
        dispatch({ type: types.SET_DEVICE_ID, payload: { deviceId } })
    }

    const setStateId = (stateId) => {
        dispatch({ type: types.SET_STATE_ID, payload: { stateId } })
    }

    const setSynchroId = (synchroId) => {
        dispatch({ type: types.SET_SYNCHRO_ID, payload: { synchroId } })
    }

    const handleDeviceIdChange = (_event, value, reason) => {
        setCurrentDeviceId(normalize(value))
        if (reason === "clear") {
            setDeviceId("")
        }
    }

    const handleVendorIdChange = (_event, value, reason) => {
        setCurrentVendorId(normalize(value))
        if (reason === "clear") {
            setStateId("")
        }
    }

    const handleSynchroIdChange = (_event, value, reason) => {
        setCurrentSynchroId(normalize(value))
        if (reason === "clear") {
            setSynchroId("")
        }
    }

    const handleDeviceIdInputChange = (_event, value, reason) => {
        if (reason === "input" || reason === "clear") {
            setCurrentDeviceId(value ?? "")
        }
    }

    const handleVendorIdInputChange = (_event, value, reason) => {
        if (reason === "input" || reason === "clear") {
            setCurrentVendorId(value ?? "")
        }
    }

    const handleSynchroIdInputChange = (_event, value, reason) => {
        if (reason === "input" || reason === "clear") {
            setCurrentSynchroId(value ?? "")
        }
    }

    const submitForm = (overrides = {}) => {
        const deviceId = normalize(overrides.deviceId ?? currentDeviceId)
        const vendorId = normalize(overrides.vendorId ?? currentVendorId)
        const synchroId = normalize(overrides.synchroId ?? currentSynchroId)

        if (
            (vendorId && vendorId === vendorIdParam) ||
            (deviceId && deviceId === deviceIdParam)
        ) {
            setOpen(true)
        }

        setCurrentDeviceId(deviceId)
        setCurrentVendorId(vendorId)
        setCurrentSynchroId(synchroId)

        setDeviceId(deviceId)
        setStateId(vendorId)
        setSynchroId(synchroId)

        navigate(
            {
                pathname: `/${game}/${action || "dashboard"}/${deviceId}`,
                search: `?${createSearchParams({
                    vendorId,
                    synchroId
                })}`
            },
            { state: deviceId }
        )
    }

    const handleEnterSubmit = (field) => (e) => {
        if (e.key !== "Enter") {
            return
        }
        e.preventDefault()
        const value = normalize(e.target.value)
        if (field === "device") {
            submitForm({ deviceId: value })
        } else if (field === "vendor") {
            submitForm({ vendorId: value })
        } else if (field === "synchro") {
            submitForm({ synchroId: value })
        }
    }

    const handleIdBlur = (setter) => (e) => {
        setter(normalize(e.target.value))
    }

    const swapDeviceAndVendor = () => {
        setCurrentDeviceId(currentVendorId ?? "")
        setCurrentVendorId(currentDeviceId ?? "")
    }

    const swapVendorAndSynchro = () => {
        setCurrentVendorId(currentSynchroId ?? "")
        setCurrentSynchroId(currentVendorId ?? "")
    }

    useEffect(() => {
        if (isCharger) {
            setSearchParams({ vendorId: "", synchroId: "" })
            setSynchroId("")
            setStateId("")
            setCurrentVendorId("")
            setCurrentSynchroId("")
        }
    }, [isCharger])

    useEffect(() => {
        setDeviceId(deviceIdParam)
        setStateId(vendorIdParam)
        setSynchroId(synchroIdParam)
    }, [deviceIdParam, vendorIdParam, synchroIdParam])

    useEffect(() => {
        if (deviceIdParam || vendorIdParam || synchroIdParam) {
            advancedSearch({
                stateId: deviceIdParam,
                vendorId: vendorIdParam,
                synchroId: synchroIdParam
            })
        }
    }, [deviceIdParam, vendorIdParam, synchroIdParam])

    useEffect(() => {
        setCurrentDeviceId(normalize(storeDeviceId))
        setCurrentVendorId(normalize(storeStateId))
        setCurrentSynchroId(normalize(storeSynchroId))
    }, [storeDeviceId, storeStateId, storeSynchroId])

    useEffect(() => {
        if (
            advancedSearchResponse.status === "fulfilled" &&
            advancedSearchResponse.data?.length === 1 &&
            !normalize(currentDeviceId)
        ) {
            setOpen(true)
        }
    }, [advancedSearchResponse])

    useEffect(() => {
        if (advancedSearchResponse.status !== "fulfilled") {
            return
        }
        const data = advancedSearchResponse.data
        if (!Array.isArray(data) || data.length === 0) {
            return
        }

        if (deviceIdParam) {
            putSearchHistoryForField(deviceIdParam, game, mode, "device")
        }
        if (vendorIdParam) {
            putSearchHistoryForField(vendorIdParam, game, mode, "vendor")
        }
        if (synchroIdParam) {
            putSearchHistoryForField(synchroIdParam, game, mode, "synchro")
        }
    }, [advancedSearchResponse.status, advancedSearchResponse.fulfilledTimeStamp])

    let searchWarning = null
    if (advancedSearchResponse?.status === "fulfilled") {
        const resultCount = advancedSearchResponse.data?.length ?? 0
        if (resultCount > 1) {
            searchWarning = getMultipleStatesWarning(setOpen)
        } else if (resultCount === 0) {
            searchWarning = getStatesNotFoundWarning()
        }
    }

    return (
        <>
            <Card sx={{ width: "100%", p: 2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "center" }}>
                    <Autocomplete
                        fullWidth
                        freeSolo
                        disabled={isMassCharge}
                        options={getSearchHistory(game, mode)}
                        value={currentDeviceId ?? ""}
                        onChange={handleDeviceIdChange}
                        onInputChange={handleDeviceIdInputChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="State ID"
                                onBlur={handleIdBlur(setCurrentDeviceId)}
                                onKeyDown={handleEnterSubmit("device")}
                            />
                        )}
                    />

                    {!isCharger && (
                        <>
                            <IconButton
                                disabled={isMassCharge}
                                onClick={swapDeviceAndVendor}
                            >
                                <SwapHorizIcon/>
                            </IconButton>
                            <Autocomplete
                                fullWidth
                                freeSolo
                                disabled={isMassCharge}
                                options={getSearchHistoryVendorId(game, mode)}
                                value={currentVendorId ?? ""}
                                onChange={handleVendorIdChange}
                                onInputChange={handleVendorIdInputChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Vendor ID"
                                        onBlur={handleIdBlur(setCurrentVendorId)}
                                        onKeyDown={handleEnterSubmit("vendor")}
                                    />
                                )}
                            />
                        </>
                    )}

                    {isRt && !isCharger && (
                        <>
                            <IconButton
                                disabled={isMassCharge}
                                onClick={swapVendorAndSynchro}
                            >
                                <SwapHorizIcon/>
                            </IconButton>
                            <Autocomplete
                                fullWidth
                                freeSolo
                                disabled={isMassCharge}
                                options={getSearchHistorySynchroId(game, mode)}
                                value={currentSynchroId ?? ""}
                                onChange={handleSynchroIdChange}
                                onInputChange={handleSynchroIdInputChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Synchro ID"
                                        onBlur={handleIdBlur(setCurrentSynchroId)}
                                        onKeyDown={handleEnterSubmit("synchro")}
                                    />
                                )}
                            />
                        </>
                    )}

                    <IconButton
                        loading={advancedSearchResponse.isLoading}
                        disabled={isMassCharge}
                        onClick={() => submitForm()}
                    >
                        <SearchIcon/>
                    </IconButton>

                    {isRt && (
                        <Loading isLoading={advancedSearchResponse.status === "pending"}>
                            {searchWarning}
                        </Loading>
                    )}
                </Stack>
            </Card>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Choose state:</DialogTitle>
                <DialogContent>
                    <AdvancedSearchTable
                        advancedSearchResponse={advancedSearchResponse}
                        closeDialog={setOpen}
                        modalView={true}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
